import { useEffect, useRef } from 'react'
import StatsController from '../controllers/StatsController'

/**
 * Hook to manage release statistics polling
 * @param {string} releaseId - Release ID
 * @param {Object} release - Release object
 * @param {Function} onUpdate - Callback when stats are updated
 * @param {boolean} isOpen - Whether the dialog is open
 */
export const useReleaseStats = (releaseId, release, onUpdate, isOpen) => {
  const isPollingRef = useRef(false)
  const onUpdateRef = useRef(onUpdate)

  // Keep callback ref up to date
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  useEffect(() => {
    if (!isOpen || !releaseId || !release) {
      return
    }

    // Poll for all releases except scheduled ones
    // Running releases need real-time updates for sending progress
    // Completed/stopped/failed releases still need updates for late webhook events (opens, clicks, etc.)
    const shouldPoll = release.status !== 'scheduled'

    if (!shouldPoll) {
      if (isPollingRef.current) {
        StatsController.stopPolling(releaseId)
        isPollingRef.current = false
      }
      return
    }

    // Start polling if not already started
    if (!isPollingRef.current) {
      // Use stable wrapper that calls latest callback
      const stableCallback = (response) => onUpdateRef.current(response)
      StatsController.startPolling(releaseId, stableCallback, 5)
      isPollingRef.current = true
    }

    // Cleanup on unmount or when release changes
    return () => {
      if (isPollingRef.current) {
        StatsController.stopPolling(releaseId)
        isPollingRef.current = false
      }
    }
  }, [isOpen, releaseId, release?.status])
}
